import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { Building03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import { FormikProps } from "formik";
import { useMask } from "@react-input/mask";
import Summary, { SummaryItem } from "./Summary";
import {
  countryOptions,
  getStateFullName,
  stateOptions,
} from "utils/addressFormat";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import { usePlacesAutoComplete } from "hooks/usePlacesAutoComplete";
import { getIn } from "formik";
import { Select, TextField } from "@outpave/ds";
interface ConfigureAddressProps {
  title?: string;
  subtitle?: string;
  formik: FormikProps<any>; // needs to be any since it can be nested or not
  parent?: string;
  customAddressName?: boolean;
  readOnly?: boolean;
  customGapSize?: any;
  isDefaultLocation?: boolean;
  ignoreCountry?: boolean;
  /** False when the address sits inside a longer form, so it does not take focus on mount. */
  autoFocus?: boolean;
  textFieldData?: {
    name: string;
    parent?: string;
    label?: string;
    required?: boolean;
    enforceValidAddress?: boolean;
    onChange?: (value: string) => void;
  };
}

const ConfigureAddress: React.FC<ConfigureAddressProps> = (props) => {
  const {
    title,
    subtitle,
    formik,
    parent,
    customAddressName = false,
    readOnly = false,
    customGapSize,
    textFieldData,
    isDefaultLocation,
    ignoreCountry = false,
    autoFocus = true,
  } = props;
  const { t } = useTranslation();

  const fieldPath = (name: string) => (parent ? `${parent}.${name}` : name);
  const getFieldValue = (name: string) =>
    getIn(formik.values, fieldPath(name) || "") ?? "";

  const { handleFormikError, handleFormikHelperText } =
    useFormikHelpers(formik);

  // The package's fields take one `errorText` string rather than MUI's
  // error boolean plus helperText pair.
  const errorTextFor = (name: string): string | undefined =>
    handleFormikError(name, { parent })
      ? (handleFormikHelperText(name, { parent }) as string | undefined)
      : undefined;

  const { addressRef, onBlur, onChange } = usePlacesAutoComplete({
    formik,
    parent: textFieldData?.parent || parent,
    customAddressName,
    enforce: textFieldData?.enforceValidAddress,
  });

  const address1 = getFieldValue("address1");
  const address2 = getFieldValue("address2");
  const city = getFieldValue("city");
  const state = getFieldValue("state");
  const zipcode = getFieldValue("zipcode");
  const country = getFieldValue("country");
  const addressName = getFieldValue("addressName");

  useEffect(() => {
    if (isDefaultLocation) {
      formik.setFieldValue(fieldPath("defaultLocation"), true);
      if (!addressName) {
        formik.setFieldValue(fieldPath("addressName"), "Default");
      }
    }

    if (ignoreCountry) {
      formik.setFieldValue(fieldPath("country"), "US");
    }

    if (!customAddressName && !textFieldData && !isDefaultLocation) {
      // Build address parts with correct comma placement
      const addressParts = [
        address1 + (!address2 ? "," : ""),
        address2 ? address2 + "," : "",
        city ? city + "," : "",
        state,
        zipcode,
        country,
      ];

      formik.setFieldValue(
        fieldPath("addressName"),
        addressParts.filter(Boolean).join(" ").trim(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    address1,
    address2,
    city,
    state,
    zipcode,
    country,
    isDefaultLocation,
    ignoreCountry,
  ]);

  useEffect(() => {
    if (autoFocus && addressRef.current && !textFieldData) {
      addressRef.current.focus();
    }
  }, [addressRef, textFieldData, autoFocus]);

  const zipMaskRef = useMask({
    mask: "_____",
    replacement: { _: /\d/ },
  });

  const summaryItems: SummaryItem[] = [
    {
      title: t("Default Location"),
      value: formik.values?.defaultLocation && t("Yes"),
    },
    {
      title: t("Location Name"),
      value: formik.values.addressName,
    },
    {
      title: t("Address"),
      value: [formik.values.address1, formik.values.address2]
        .filter(Boolean)
        .join(" "),
      fullWidth: true,
    },
    {
      title: t("City"),
      value: formik.values.city,
    },
    {
      title: t("State"),
      value: getStateFullName(formik.values.state),
    },
    {
      title: t("Zip Code"),
      value: formik.values.zipcode,
    },
    {
      title: t("Country"),
      value: formik.values.country,
    },
  ];

  const gapClass = customGapSize ? customGapSize : "24px";

  return (
    <>
      {textFieldData ? (
        <>
          <TextField
            ref={addressRef}
            key={textFieldData.name}
            name={textFieldData.name}
            label={textFieldData.label ? t(textFieldData.label) : undefined}
            required={textFieldData.required}
            value={getFieldValue(textFieldData.name)}
            onChange={onChange}
            onBlur={onBlur}
            errorText={errorTextFor(textFieldData.name)}
          />
        </>
      ) : (
        <Stack gap="var(--space-6)" sx={{ flex: "1 1 auto" }}>
          {(title || subtitle) && (
            <Stack gap="var(--space-2)">
              {title && (
                <Inline gap="var(--space-2)" align="center">
                  <HugeiconsIcon
                    icon={Building03Icon}
                    size={20}
                    color="var(--color-text-secondary)"
                  />
                  <Typography variant="h4">{t(`${title}`)}</Typography>
                </Inline>
              )}
              {subtitle && (
                <Typography
                  variant="body2"
                  sx={{ color: "var(--color-text-secondary)" }}
                >
                  {t(`${subtitle}`)}
                </Typography>
              )}
            </Stack>
          )}
          {readOnly && (
            <Summary
              items={summaryItems}
              loading={!getFieldValue("address1")}
              disableContainer
            />
          )}

          <Stack
            gap={gapClass}
            // Not textFieldData: this is the else branch of the ternary that
            // tests it, so it is falsy here by construction.
            sx={{ display: readOnly ? "none" : "flex" }}
          >
            {customAddressName && (
              <TextField
                name={fieldPath("addressName")}
                label={t("Location Name")}
                required
                placeholder={t("ie Home") as string}
                fullWidth
                value={getFieldValue("addressName")}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errorText={errorTextFor("addressName")}
              />
            )}

            <TextField
              ref={addressRef}
              name={fieldPath("address1")}
              label={t("Address")}
              required
              fullWidth
              autoComplete="on"
              value={getFieldValue("address1") || ""}
              onChange={formik.handleChange}
              errorText={errorTextFor("address1")}
            />

            <TextField
              name={fieldPath("address2")}
              label={t("Address 2")}
              fullWidth
              value={getFieldValue("address2")}
              onChange={formik.handleChange}
              errorText={errorTextFor("address2")}
            />

            <TextField
              name={fieldPath("city")}
              label={t("City")}
              required
              fullWidth
              value={getFieldValue("city")}
              onChange={formik.handleChange}
              errorText={errorTextFor("city")}
            />

            <Columns count={2} gap="var(--space-5)">
              <Select
                label={t("State")}
                required
                fullWidth
                options={stateOptions.map((option) => ({
                  id: option.value,
                  label: option.label,
                }))}
                value={getFieldValue("state") || null}
                onChange={(id) => formik.setFieldValue(fieldPath("state"), id)}
                errorText={errorTextFor("state")}
              />
              <TextField
                name={fieldPath("zipcode")}
                label={t("Zip Code")}
                required
                type="text"
                fullWidth
                ref={zipMaskRef}
                inputMode="numeric"
                value={getFieldValue("zipcode")}
                onChange={formik.handleChange}
                errorText={errorTextFor("zipcode")}
              />
            </Columns>

            {!ignoreCountry && (
              <Select
                label={t("Country")}
                required
                fullWidth
                options={countryOptions.map((option) => ({
                  id: option.value,
                  label: option.label,
                }))}
                value={getFieldValue("country") || null}
                onChange={(id) =>
                  formik.setFieldValue(fieldPath("country"), id)
                }
                errorText={errorTextFor("country")}
              />
            )}
          </Stack>
        </Stack>
      )}
    </>
  );
};

export default ConfigureAddress;
